const isEffectivelyEmpty = (value) => {
  return (
    value === null ||
    value === undefined ||
    value === '' ||
    (Array.isArray(value) && value.length === 0) ||
    (typeof value === 'object' &&
      value !== null &&
      Object.keys(value).length === 0)
  );
};

const areValuesSemanticallySame = (value1, value2) => {
  if (isEffectivelyEmpty(value1) && isEffectivelyEmpty(value2)) {
    return true;
  }

  if (isEffectivelyEmpty(value1) !== isEffectivelyEmpty(value2)) {
    return false;
  }

  if (typeof value1 === 'number' && typeof value2 === 'string') {
    return value1 === Number(value2);
  }

  if (typeof value1 === 'string' && typeof value2 === 'number') {
    return Number(value1) === value2;
  }

  if (typeof value1 === 'boolean' && typeof value2 === 'string') {
    return value1 === (value2.toLowerCase() === 'true');
  }

  if (typeof value1 === 'string' && typeof value2 === 'boolean') {
    return (value1.toLowerCase() === 'true') === value2;
  }

  if (Array.isArray(value1) && Array.isArray(value2)) {
    if (value1.length !== value2.length) return false;

    for (let i = 0; i < value1.length; i++) {
      if (!areValuesSemanticallySame(value1[i], value2[i])) {
        return false;
      }
    }
    return true;
  }

  if (
    typeof value1 === 'object' &&
    value1 !== null &&
    typeof value2 === 'object' &&
    value2 !== null
  ) {
    return JSON.stringify(value1) === JSON.stringify(value2);
  }

  return value1 === value2;
};

const compareGoalsArrays = (originalGoals, newGoals, studentGoalsData) => {
  const changes = [];
  const originalGoalIds = new Set(
    (originalGoals || []).map((g) => g.goalID || g.goalId)
  );
  const newGoalIds = new Set((newGoals || []).map((g) => g.goalID || g.goalId));

  // Helper to get goal name from studentGoalsData
  const getGoalName = (goalId) => {
    for (const student of studentGoalsData || []) {
      const goal = (student?.dataCollection || []).find(
        (g) => (g?.id || g?.docId) === goalId
      );
      if (goal) {
        return (
          goal?.info?.GoalDetails?.GoalTitle ||
          goal?.info?.GoalDetails?.GoalName ||
          `Goal (${goalId})`
        );
      }
    }
    return `Goal (${goalId})`;
  };

  // Check for added goals
  newGoalIds.forEach((goalId) => {
    if (!originalGoalIds.has(goalId)) {
      const goalName = getGoalName(goalId);
      changes.push({
        type: 'added',
        fieldName: 'Goals',
        oldValue: '',
        newValue: goalName,
      });
    }
  });

  // Check for removed goals
  originalGoalIds.forEach((goalId) => {
    if (!newGoalIds.has(goalId)) {
      const goalName = getGoalName(goalId);
      changes.push({
        type: 'removed',
        fieldName: 'Goals',
        oldValue: goalName,
        newValue: '',
      });
    }
  });

  return changes;
};

/**
 * Compare quick tracks arrays and return changes with track names
 * @param {Array} originalQuickTracks - Original quick tracks array [{quickTrackID, dataEntryID}]
 * @param {Array} newQuickTracks - New quick tracks array [{quickTrackID, dataEntryID}]
 * @param {Array} studentBehaviorData - Student behavior data to get track names
 * @returns {Array} Array of change objects
 */
const compareQuickTracksArrays = (
  originalQuickTracks,
  newQuickTracks,
  studentBehaviorData
) => {
  const changes = [];
  const originalTrackIds = new Set(
    (originalQuickTracks || []).map((t) => t.quickTrackID || t.quickTrackId)
  );
  const newTrackIds = new Set(
    (newQuickTracks || []).map((t) => t.quickTrackID || t.quickTrackId)
  );

  // Helper to get quick track name from studentBehaviorData
  const getQuickTrackName = (trackId) => {
    for (const student of studentBehaviorData || []) {
      const behavior = (student?.behaviors || []).find(
        (b) => (b?.id || b?.docId) === trackId
      );
      if (behavior) {
        const behaviorDetails = behavior?.info?.BehaviorDetails;
        return (
          behaviorDetails?.Type?.BehaviorName ||
          behaviorDetails?.Type?.SkillName ||
          `QuickTrack (${trackId})`
        );
      }
    }
    return `QuickTrack (${trackId})`;
  };

  // Check for added quick tracks
  newTrackIds.forEach((trackId) => {
    if (!originalTrackIds.has(trackId)) {
      const trackName = getQuickTrackName(trackId);
      changes.push({
        type: 'added',
        fieldName: 'QuickTracks',
        oldValue: '',
        newValue: trackName,
      });
    }
  });

  // Check for removed quick tracks
  originalTrackIds.forEach((trackId) => {
    if (!newTrackIds.has(trackId)) {
      const trackName = getQuickTrackName(trackId);
      changes.push({
        type: 'removed',
        fieldName: 'QuickTracks',
        oldValue: trackName,
        newValue: '',
      });
    }
  });

  return changes;
};

const compareNestedObjects = (original, updated, prefix = '') => {
  const changes = [];
  const allKeys = new Set([
    ...Object.keys(original || {}),
    ...Object.keys(updated || {}),
  ]);

  allKeys.forEach((key) => {
    const originalValue = original?.[key];
    const updatedValue = updated?.[key];
    const fieldName = prefix ? `${prefix} > ${key}` : key;

    // Skip if values are the same
    if (areValuesSemanticallySame(originalValue, updatedValue)) {
      return;
    }

    // If both are objects (but not arrays), recurse
    if (
      typeof originalValue === 'object' &&
      originalValue !== null &&
      !Array.isArray(originalValue) &&
      typeof updatedValue === 'object' &&
      updatedValue !== null &&
      !Array.isArray(updatedValue)
    ) {
      changes.push(
        ...compareNestedObjects(originalValue, updatedValue, fieldName)
      );
    } else {
      // Simple value change
      changes.push({
        type: 'modified',
        fieldName,
        oldValue: originalValue,
        newValue: updatedValue,
      });
    }
  });

  return changes;
};

export const trackSessionChanges = (
  originalData,
  newSessionObject,
  studentGoalsData = [],
  studentBehaviorData = []
) => {
  if (!originalData) {
    return []; // No changes to track for new sessions
  }

  const changes = [];

  // Compare Goals
  const goalChanges = compareGoalsArrays(
    originalData.Goals,
    newSessionObject.Goals,
    studentGoalsData
  );
  changes.push(...goalChanges);

  // Compare QuickTracks
  const quickTrackChanges = compareQuickTracksArrays(
    originalData.QuickTracks,
    newSessionObject.QuickTracks,
    studentBehaviorData
  );
  changes.push(...quickTrackChanges);

  // Compare ClinicalSessionDetails
  const detailsChanges = compareNestedObjects(
    originalData.ClinicalSessionDetails,
    newSessionObject.ClinicalSessionDetails,
    'Session Details'
  );
  changes.push(...detailsChanges);

  // Compare BillingInformation
  const billingChanges = compareNestedObjects(
    originalData.BillingInformation,
    newSessionObject.BillingInformation,
    'Billing Information'
  );
  changes.push(...billingChanges);

  // Compare ShareWithTeam
  const shareChanges = compareNestedObjects(
    originalData.ShareWithTeam,
    newSessionObject.ShareWithTeam,
    'Share With Team'
  );
  changes.push(...shareChanges);

  // Compare simple fields
  const simpleFields = ['SessionSummary', 'Observations'];
  simpleFields.forEach((field) => {
    if (
      !areValuesSemanticallySame(originalData[field], newSessionObject[field])
    ) {
      changes.push({
        type: 'modified',
        fieldName: field,
        oldValue: originalData[field],
        newValue: newSessionObject[field],
      });
    }
  });

  return changes;
};
